/**
 * Visor de documentos: ver un contrato, un ticket o un DNI SIN descargarlo.
 *
 * Antes, pulsar un documento lo bajaba al disco, y para echar un vistazo a un ticket había que
 * buscarlo en Descargas, abrirlo y borrarlo después. Ahora se abre encima de la pantalla, y
 * desde ahí se descarga si de verdad hace falta.
 *
 * Se abre desde cualquier sitio con `useDocumentViewer().open({ id, name })`: el proveedor va en
 * la raíz de la aplicación (`main.tsx`).
 *
 * SEGURIDAD. La API sirve los documentos como descarga (`attachment`) a propósito: un PDF o un
 * HTML renderizado DESDE nuestro origen podría ejecutar código con la sesión abierta. Aquí el
 * fichero se pide con la sesión, se guarda en memoria y se enseña SOLO si es de un tipo que el
 * navegador pinta sin ejecutar nada nuestro:
 *
 *  · PDF → en un `<iframe>`, que el navegador entrega a su visor de PDF (aislado de la página).
 *  · Imagen (JPEG, PNG, WebP, GIF, AVIF) → en un `<img>`, donde ni un SVG ejecuta código.
 *  · Cualquier otra cosa (Word, Excel, HTML…) → no se pinta: se ofrece descargarla.
 *
 * El blob se crea con el tipo que decidimos nosotros, no con el que diga el fichero, y se libera
 * al cerrar.
 *
 * Lo demás, como la vista ampliada de fotos: Esc cierra, el foco entra y vuelve a donde estaba,
 * y el fondo no se desplaza mientras está abierto.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { api } from '../../api/client';
import { Icon } from '../Icon';
import { toast } from '../feedback/Toaster';
import styles from './DocumentViewer.module.css';

export interface ViewerDocument {
  id: string;
  name: string;
  /** Una línea debajo del nombre: «Contrato · PU5 · 20/10/26». */
  meta?: string | null;
}

interface ViewerContextValue {
  open: (document: ViewerDocument) => void;
}

const ViewerContext = createContext<ViewerContextValue | null>(null);

/** Abre el visor. Fuera del proveedor (pruebas, portal), cae en descargar el fichero. */
export function useDocumentViewer(): ViewerContextValue {
  const context = useContext(ViewerContext);
  return (
    context ?? {
      open: (document) => {
        void api.downloadDocument(document.id, document.name).catch(() => {
          toast.error('No se ha podido descargar el documento.');
        });
      },
    }
  );
}

export function DocumentViewerProvider({ children }: { children: ReactNode }) {
  const [actual, setActual] = useState<ViewerDocument | null>(null);
  const value = useMemo<ViewerContextValue>(() => ({ open: setActual }), []);
  const cerrar = useCallback(() => setActual(null), []);
  return (
    <ViewerContext.Provider value={value}>
      {children}
      {actual && <DocumentViewer key={actual.id} document={actual} onClose={cerrar} />}
    </ViewerContext.Provider>
  );
}

/**
 * El ojo: abre el documento en el visor. Para poner al lado de un documento en una lista o una
 * tabla; no propaga el clic, así que dentro de una fila pulsable no abre también la fila.
 */
export function ViewDocumentButton({ document: doc, className }: { document: ViewerDocument; className?: string }) {
  const visor = useDocumentViewer();
  return (
    <button
      type="button"
      className={className ? `${styles.ojo} ${className}` : styles.ojo}
      onClick={(event) => {
        event.stopPropagation();
        visor.open(doc);
      }}
      onKeyDown={(event) => event.stopPropagation()}
      aria-label={`Ver ${doc.name}`}
      title="Ver el documento"
    >
      <Icon name="eye" size={17} strokeWidth={2} />
    </button>
  );
}

type Cargado = { url: string | null; nombre: string; tipo: string; bytes: number; blob: Blob };

type Vista =
  | { estado: 'cargando' }
  | { estado: 'error'; mensaje: string }
  | ({ estado: 'pdf' | 'imagen' | 'otro' } & Cargado);

const IMAGENES = new Set(['image/jpeg', 'image/png', 'image/webp', 'image/gif', 'image/avif']);

const MIME_IMAGEN: Record<string, string> = {
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  png: 'image/png',
  webp: 'image/webp',
  gif: 'image/gif',
  avif: 'image/avif',
};

const extensionDe = (nombre: string) => /\.([a-z0-9]+)$/i.exec(nombre)?.[1]?.toLowerCase() ?? '';

/** El tipo que se va a pintar, por la cabecera y, si no la trae, por la extensión. */
function tipoVisible(tipo: string, nombre: string): 'pdf' | 'imagen' | 'otro' {
  const extension = extensionDe(nombre);
  const generico = tipo === '' || tipo === 'application/octet-stream';
  if (tipo === 'application/pdf' || (generico && extension === 'pdf')) return 'pdf';
  if (IMAGENES.has(tipo)) return 'imagen';
  if (generico && extension in MIME_IMAGEN) return 'imagen';
  return 'otro';
}

function tamano(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1).replace('.', ',')} MB`;
}

function DocumentViewer({ document: doc, onClose }: { document: ViewerDocument; onClose: () => void }) {
  const [vista, setVista] = useState<Vista>({ estado: 'cargando' });
  const [intento, setIntento] = useState(0);
  const [ampliada, setAmpliada] = useState(false);
  const dialogRef = useRef<HTMLDivElement>(null);
  const openerRef = useRef<Element | null>(null);

  // --- Cargar el fichero --------------------------------------------------------------
  useEffect(() => {
    let vivo = true;
    let url: string | null = null;
    setVista({ estado: 'cargando' });
    api
      .fetchDocument(doc.id, doc.name)
      .then(({ blob, name, type }) => {
        if (!vivo) return;
        const clase = tipoVisible(type, name);
        if (clase === 'otro') {
          setVista({ estado: 'otro', url: null, nombre: name, tipo: type, bytes: blob.size, blob });
          return;
        }
        // El tipo lo fijamos nosotros: un fichero que dice ser PDF se pinta como PDF y nada más.
        const mime =
          clase === 'pdf' ? 'application/pdf' : IMAGENES.has(type) ? type : (MIME_IMAGEN[extensionDe(name)] ?? 'image/jpeg');
        url = URL.createObjectURL(new Blob([blob], { type: mime }));
        setVista({ estado: clase, url, nombre: name, tipo: mime, bytes: blob.size, blob });
      })
      .catch((error: unknown) => {
        if (!vivo) return;
        setVista({ estado: 'error', mensaje: error instanceof Error ? error.message : 'No se ha podido abrir el documento.' });
      });
    return () => {
      vivo = false;
      if (url) URL.revokeObjectURL(url);
    };
  }, [doc.id, doc.name, intento]);

  // --- Foco, Esc y scroll del fondo ------------------------------------------------------
  useEffect(() => {
    openerRef.current = document.activeElement;
    dialogRef.current?.focus();
    const overflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        // En captura y sin propagar: si el visor se abrió desde un panel, Esc cierra el visor y
        // no también el panel de debajo.
        event.stopPropagation();
        onClose();
      }
    }
    document.addEventListener('keydown', onKeyDown, true);
    return () => {
      document.removeEventListener('keydown', onKeyDown, true);
      document.body.style.overflow = overflow;
      const opener = openerRef.current;
      if (opener instanceof HTMLElement && opener.isConnected) opener.focus();
    };
  }, [onClose]);

  const cargado: Cargado | null = vista.estado === 'pdf' || vista.estado === 'imagen' || vista.estado === 'otro' ? vista : null;

  const descargar = useCallback(() => {
    if (cargado) {
      // Ya está en memoria: se guarda sin volver a pedirlo.
      const url = URL.createObjectURL(cargado.blob);
      const enlace = document.createElement('a');
      enlace.href = url;
      enlace.download = cargado.nombre;
      document.body.appendChild(enlace);
      enlace.click();
      enlace.remove();
      URL.revokeObjectURL(url);
      return;
    }
    void api.downloadDocument(doc.id, doc.name).catch(() => toast.error('No se ha podido descargar el documento.'));
  }, [cargado, doc.id, doc.name]);

  const abrirAparte = useCallback(() => {
    if (cargado?.url) window.open(cargado.url, '_blank', 'noopener');
  }, [cargado]);

  const nombre = cargado?.nombre ?? doc.name;
  const detalle = [
    doc.meta,
    vista.estado === 'pdf' ? 'PDF' : vista.estado === 'imagen' ? 'Imagen' : null,
    cargado ? tamano(cargado.bytes) : null,
  ]
    .filter(Boolean)
    .join(' · ');

  return createPortal(
    <div className={styles.capa} onMouseDown={(event) => event.target === event.currentTarget && onClose()}>
      <div
        ref={dialogRef}
        className={styles.visor}
        role="dialog"
        aria-modal="true"
        aria-label={`Documento: ${nombre}`}
        tabIndex={-1}
      >
        <header className={styles.cabecera}>
          <span className={styles.icono} aria-hidden="true">
            <Icon name={vista.estado === 'imagen' ? 'image' : 'file-lines'} size={18} />
          </span>
          <div className={styles.titulo}>
            <span className={styles.nombre} title={nombre}>
              {nombre}
            </span>
            {detalle && <span className={styles.meta}>{detalle}</span>}
          </div>
          <div className={styles.acciones}>
            {(vista.estado === 'pdf' || vista.estado === 'imagen') && (
              <button type="button" className={styles.boton} onClick={abrirAparte} title="Abrir en una pestaña nueva">
                <Icon name="expand" size={15} strokeWidth={2} />
                <span className={styles.botonTexto}>Abrir aparte</span>
              </button>
            )}
            <button type="button" className={`${styles.boton} ${styles.botonFuerte}`} onClick={descargar}>
              <Icon name="download" size={15} strokeWidth={2} />
              <span className={styles.botonTexto}>Descargar</span>
            </button>
            <button type="button" className={styles.cerrar} onClick={onClose} aria-label="Cerrar el visor" title="Cerrar (Esc)">
              <Icon name="close" size={18} strokeWidth={2} />
            </button>
          </div>
        </header>

        <div className={styles.cuerpo}>
          {vista.estado === 'cargando' && (
            <div className={styles.aviso} role="status">
              <span className={styles.cargando} aria-hidden="true" />
              Abriendo el documento…
            </div>
          )}

          {vista.estado === 'error' && (
            <div className={styles.aviso} role="alert">
              <strong>No se ha podido abrir.</strong>
              <span>{vista.mensaje}</span>
              <button type="button" className={styles.boton} onClick={() => setIntento((n) => n + 1)}>
                Reintentar
              </button>
            </div>
          )}

          {vista.estado === 'pdf' && vista.url && (
            <iframe className={styles.pdf} src={`${vista.url}#view=FitH`} title={nombre} />
          )}

          {vista.estado === 'imagen' && vista.url && (
            <div className={ampliada ? `${styles.lienzo} ${styles.lienzoAmpliado}` : styles.lienzo}>
              <img
                src={vista.url}
                alt={nombre}
                className={styles.imagen}
                onClick={() => setAmpliada((valor) => !valor)}
                title={ampliada ? 'Ajustar a la pantalla' : 'Ver a tamaño real'}
                onError={() =>
                  setVista((actual) => (actual.estado === 'imagen' ? { ...actual, estado: 'otro', url: null } : actual))
                }
              />
            </div>
          )}

          {vista.estado === 'otro' && (
            <div className={styles.aviso}>
              <span className={styles.iconoGrande} aria-hidden="true">
                <Icon name="file" size={30} strokeWidth={1.6} />
              </span>
              <strong>Este tipo de archivo no se puede ver aquí.</strong>
              <span>Se pueden ver los PDF y las fotos. Descárgalo para abrirlo con su programa.</span>
              <button type="button" className={`${styles.boton} ${styles.botonFuerte}`} onClick={descargar}>
                <Icon name="download" size={15} strokeWidth={2} />
                Descargar
              </button>
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}
